import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Loader2, X } from "lucide-react";
import { authClient } from "@/clients/authClient";
import { FeatureCard } from "@/lib/components/FeatureCard";
import { PlaceMiniMap } from "@/lib/components/map/PlaceMiniMap";
import { useIsAdmin } from "@/lib/hooks/useIsAdmin";
import { getCategoryIcon } from "@/lib/utils/categoryIcons";
import { trpc } from "@/router";
import { ActionBar } from "../ActionBar";

// ─── Sort ───────────────────────────────────────────────────────────────────
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const FEATURE_SORT_ORDER = {
  Huvudentré: 0,
  Sidoentré: 1,
  Parkering: 2,
  Service: 3,
  Nödutgång: 4
};
function sortFeatures(features) {
  return [...features].sort((a, b) => {
    const aOrder = FEATURE_SORT_ORDER[a.featureType?.name] ?? 99;
    const bOrder = FEATURE_SORT_ORDER[b.featureType?.name] ?? 99;
    return aOrder - bOrder;
  });
}

// ─── PlaceDetail ────────────────────────────────────────────────────────────

export function PlaceDetail(t0) {
  const $ = _c(78);
  const {
    placeId,
    onClose,
    onReport
  } = t0;
  const {
    data: session
  } = authClient.useSession();
  const currentUserId = session?.user?.id;
  const isAdmin = useIsAdmin();
  let t1;
  if ($[0] !== placeId) {
    t1 = trpc.places.byId.queryOptions({
      id: placeId
    });
    $[0] = placeId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: place,
    isLoading
  } = useQuery(t1);
  let t2;
  if ($[2] !== placeId) {
    t2 = trpc.places.byIdFull.queryOptions({
      id: placeId
    });
    $[2] = placeId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: fullPlace
  } = useQuery(t2);
  if (isLoading) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /*#__PURE__*/_jsx("div", {
        className: "flex items-center justify-center py-12",
        children: /*#__PURE__*/_jsx(Loader2, {
          className: "h-6 w-6 animate-spin text-gray-400"
        })
      });
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (!place) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /*#__PURE__*/_jsx("div", {
        className: "p-4 text-sm text-gray-500",
        children: "Platsen hittades inte."
      });
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let otherPendingCount;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[6] !== currentUserId || $[7] !== fullPlace?.features || $[8] !== isAdmin || $[9] !== onClose || $[10] !== onReport || $[11] !== place.categoryIcon || $[12] !== place.categoryName || $[13] !== place.description || $[14] !== place.location || $[15] !== place.name || $[16] !== placeId) {
    const allFeatures = fullPlace?.features ?? [];
    const visibleFeatures = isAdmin ? allFeatures : allFeatures.filter(e => e.status === "approved" || e.status === "pending" && e.createdBy === currentUserId);
    otherPendingCount = isAdmin ? 0 : allFeatures.filter(e_0 => e_0.status === "pending" && e_0.createdBy !== currentUserId).length;
    const hasVerified = allFeatures.some(_temp);
    const hasPending = !hasVerified && allFeatures.some(_temp2);
    let t9;
    if ($[24] !== place.categoryIcon) {
      t9 = getCategoryIcon(place.categoryIcon);
      $[24] = place.categoryIcon;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    const Icon = t9;
    const featurePins = visibleFeatures.filter(_temp3).map(_temp4);
    t3 = "p-4";
    const t10 = hasVerified ? "bg-green-100" : hasPending ? "bg-amber-100" : "bg-slate-100";
    let t11;
    if ($[26] !== t10) {
      t11 = cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", t10);
      $[26] = t10;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    const t12 = hasVerified ? "text-green-600" : hasPending ? "text-amber-500" : "text-slate-500";
    let t13;
    if ($[28] !== t12) {
      t13 = cn("h-5 w-5", t12);
      $[28] = t12;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== Icon || $[31] !== t13) {
      t14 = /*#__PURE__*/_jsx(Icon, {
        className: t13
      });
      $[30] = Icon;
      $[31] = t13;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] !== t11 || $[34] !== t14) {
      t15 = /*#__PURE__*/_jsx("div", {
        className: t11,
        children: t14
      });
      $[33] = t11;
      $[34] = t14;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    let t16;
    if ($[36] !== place.name) {
      t16 = /*#__PURE__*/_jsx("h3", {
        className: "text-lg font-bold leading-tight text-gray-900",
        children: place.name
      });
      $[36] = place.name;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    let t17;
    if ($[38] !== place.categoryName) {
      t17 = place.categoryName && /*#__PURE__*/_jsx("p", {
        className: "text-sm text-gray-500",
        children: place.categoryName
      });
      $[38] = place.categoryName;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    if ($[40] !== t16 || $[41] !== t17) {
      t18 = /*#__PURE__*/_jsxs("div", {
        children: [t16, t17]
      });
      $[40] = t16;
      $[41] = t17;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    let t19;
    if ($[43] !== t15 || $[44] !== t18) {
      t19 = /*#__PURE__*/_jsxs("div", {
        className: "flex items-center gap-3",
        children: [t15, t18]
      });
      $[43] = t15;
      $[44] = t18;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    let t20;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = /*#__PURE__*/_jsx(X, {
        className: "h-4 w-4"
      });
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    let t21;
    if ($[47] !== onClose) {
      t21 = /*#__PURE__*/_jsx("button", {
        onClick: onClose,
        className: "rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600",
        children: t20
      });
      $[47] = onClose;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    if ($[49] !== t19 || $[50] !== t21) {
      t4 = /*#__PURE__*/_jsxs("div", {
        className: "mb-1 flex items-start justify-between",
        children: [t19, t21]
      });
      $[49] = t19;
      $[50] = t21;
      $[51] = t4;
    } else {
      t4 = $[51];
    }
    if ($[52] !== place.description) {
      t5 = place.description && /*#__PURE__*/_jsx("p", {
        className: "mt-3 text-sm leading-relaxed text-gray-600",
        children: place.description
      });
      $[52] = place.description;
      $[53] = t5;
    } else {
      t5 = $[53];
    }
    let t22;
    if ($[54] !== place.name || $[55] !== placeId) {
      t22 = {
        placeId,
        placeName: place.name
      };
      $[54] = place.name;
      $[55] = placeId;
      $[56] = t22;
    } else {
      t22 = $[56];
    }
    let t23;
    if ($[57] !== onReport || $[58] !== place.name || $[59] !== placeId) {
      t23 = () => onReport(placeId, place.name);
      $[57] = onReport;
      $[58] = place.name;
      $[59] = placeId;
      $[60] = t23;
    } else {
      t23 = $[60];
    }
    if ($[61] !== place.location || $[62] !== t22 || $[63] !== t23) {
      t6 = /*#__PURE__*/_jsx("div", {
        className: "mt-3",
        children: /*#__PURE__*/_jsx(ActionBar, {
          location: place.location,
          bidra: t22,
          onBidra: t23
        })
      });
      $[61] = place.location;
      $[62] = t22;
      $[63] = t23;
      $[64] = t6;
    } else {
      t6 = $[64];
    }
    t7 = featurePins.length > 0 && /*#__PURE__*/_jsx("div", {
      className: "mt-4 h-40 overflow-hidden rounded-lg border",
      children: /*#__PURE__*/_jsx(PlaceMiniMap, {
        latitude: place.location.latitude,
        longitude: place.location.longitude,
        features: featurePins
      })
    });
    t8 = visibleFeatures.length > 0 && /*#__PURE__*/_jsxs("div", {
      className: "mt-4",
      children: [/*#__PURE__*/_jsx("div", {
        className: "space-y-1.5",
        children: sortFeatures(visibleFeatures).slice(0, 3).map(_temp5)
      }), visibleFeatures.length > 3 && /*#__PURE__*/_jsxs("p", {
        className: "mt-1.5 text-xs text-gray-400",
        children: ["+", visibleFeatures.length - 3, " fler"]
      })]
    });
    $[6] = currentUserId;
    $[7] = fullPlace?.features;
    $[8] = isAdmin;
    $[9] = onClose;
    $[10] = onReport;
    $[11] = place.categoryIcon;
    $[12] = place.categoryName;
    $[13] = place.description;
    $[14] = place.location;
    $[15] = place.name;
    $[16] = placeId;
    $[17] = otherPendingCount;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    otherPendingCount = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
  }
  let t9;
  if ($[65] !== otherPendingCount) {
    t9 = otherPendingCount > 0 && /*#__PURE__*/_jsx("p", {
      className: "mt-2 text-xs text-amber-600",
      children: otherPendingCount === 1 ? "1 bidrag v\xE4ntar p\xE5 granskning" : `${otherPendingCount} bidrag väntar på granskning`
    });
    $[65] = otherPendingCount;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  const t10 = String(placeId);
  let t11;
  if ($[67] !== t10) {
    t11 = /*#__PURE__*/_jsx(Link, {
      to: "/plats/$id",
      params: {
        id: t10
      },
      className: "mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg border py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50",
      children: "Visa allt om platsen"
    });
    $[67] = t10;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  let t12;
  if ($[69] !== t11 || $[70] !== t3 || $[71] !== t4 || $[72] !== t5 || $[73] !== t6 || $[74] !== t7 || $[75] !== t8 || $[76] !== t9) {
    t12 = /*#__PURE__*/_jsxs("div", {
      className: t3,
      children: [t4, t5, t6, t7, t8, t9, t11]
    });
    $[69] = t11;
    $[70] = t3;
    $[71] = t4;
    $[72] = t5;
    $[73] = t6;
    $[74] = t7;
    $[75] = t8;
    $[76] = t9;
    $[77] = t12;
  } else {
    t12 = $[77];
  }
  return t12;
}
function _temp5(feature) {
  return /*#__PURE__*/_jsx(FeatureCard, {
    feature: feature
  }, feature.id);
}
function _temp4(e_4) {
  return {
    id: e_4.id,
    label: e_4.featureType?.name ?? "Entr\xE9",
    latitude: e_4.location.latitude,
    longitude: e_4.location.longitude,
    status: e_4.status
  };
}
function _temp3(e_3) {
  return e_3.location;
}
function _temp2(e_2) {
  return e_2.status === "pending";
}
function _temp(e_1) {
  return e_1.status === "approved";
}